import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import {
  findDuplicate,
  normalizeNotificationInput,
  notificationSignature,
  toastDuration,
} from '../utils/notificationPolicy'

const NotificationContext = createContext()

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([])
  // Signatures récentes : évite qu’un double rendu crée deux fois la même alerte
  const recentRef = useRef(new Map())

  const forgetSignature = useCallback((id) => {
    for (const [signature, entry] of recentRef.current) {
      if (entry.id === id) recentRef.current.delete(signature)
    }
  }, [])

  const removeNotification = useCallback((id) => {
    forgetSignature(id)
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [forgetSignature])

  const addNotification = useCallback((notification, type) => {
    const normalized = normalizeNotificationInput(notification, type)
    if (!normalized) return null

    const signature = notificationSignature(normalized)
    const now = Date.now()
    const duplicate = findDuplicate(recentRef.current, signature, now)
    if (duplicate) return duplicate.id

    const id = now + Math.random()
    const newNotification = {
      id,
      type: normalized.type || 'info',
      title: normalized.title || '',
      message: normalized.message || '',
      read: false,
      timestamp: new Date(),
      ...normalized,
    }
    // La durée voyage avec la notification : le toast pilote lui-même son expiration
    newNotification.toastDuration = toastDuration(newNotification)

    recentRef.current.set(signature, { id, at: now })
    if (recentRef.current.size > 40) {
      const oldest = recentRef.current.keys().next().value
      recentRef.current.delete(oldest)
    }

    setNotifications(prev => [newNotification, ...prev])
    return id
  }, [])

  /** Retire le toast sans effacer la notification du centre. */
  const dismissToast = useCallback((id) => {
    setNotifications(prev => (
      prev.map(n => (n.id === id ? { ...n, toastDismissed: true } : n))
    ))
  }, [])

  const markAsRead = useCallback((id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    )
  }, [])

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }, [])

  const clearByIds = useCallback((ids) => {
    const idSet = new Set(ids || [])
    if (idSet.size === 0) return
    idSet.forEach((id) => forgetSignature(id))
    setNotifications((prev) => prev.filter((item) => !idSet.has(item.id)))
  }, [forgetSignature])

  const restoreNotifications = useCallback((items = []) => {
    setNotifications((prev) => {
      const existing = new Set(prev.map((item) => item.id))
      const restored = items.filter((item) => item && !existing.has(item.id))
      return restored.length ? [...restored, ...prev] : prev
    })
  }, [])

  const clearAll = useCallback(() => {
    setNotifications((prev) => {
      prev.forEach((item) => forgetSignature(item.id))
      return []
    })
  }, [forgetSignature])

  const unreadCount = notifications.filter(n => !n.read).length

  useEffect(() => {
    const onTest = (event) => {
      const detail = event.detail && typeof event.detail === 'object' ? event.detail : {}
      addNotification({
        type: detail.type || 'info',
        title: detail.title || 'Notification de test',
        message: detail.message || 'Entrée destinée aux essais de glissement.',
        showToast: detail.showToast !== false,
        autoDismiss: detail.autoDismiss,
        duration: detail.duration,
      })
    }
    window.addEventListener('camit:test-notification', onTest)
    return () => window.removeEventListener('camit:test-notification', onTest)
  }, [addNotification])

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      removeNotification,
      dismissToast,
      markAsRead,
      markAllAsRead,
      clearAll,
      clearByIds,
      restoreNotifications,
      unreadCount
    }}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return context
}
